import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  Output,
  QueryList,
  ViewChild,
  ViewChildren,
  ChangeDetectionStrategy,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';

import { TranslocoModule } from '@jsverse/transloco';

import { CapitalizePipe } from '@hmax-order/frontend/utils';
import { ButtonConfig } from '../../../interfaces';
import { ButtonComponent } from '../button/button.component';

@Component({
  selector: 'hmax-button-group',
  standalone: true,
  templateUrl: './button-group.component.html',
  styleUrls: ['./button-group.component.scss'],
  imports: [CommonModule, ButtonComponent, MatButtonModule, MatMenuModule, MatIconModule, CapitalizePipe, TranslocoModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [CapitalizePipe],
})
export class ButtonGroupComponent implements AfterViewInit, OnDestroy {
  private _cd = inject(ChangeDetectorRef);

  @Input() buttons: ButtonConfig[] = [];
  @Input() align: 'left' | 'right' = 'left';
  @Input() useMenu = false; // Adiciona a opção para controlar o uso de mat-menu
  @Output() buttonClick = new EventEmitter<string>();
  @ViewChild('buttonContainer', { static: true }) buttonContainer!: ElementRef;
  @ViewChildren('hmaxButton') buttonElements!: QueryList<ElementRef>;

  private _resizeObserver!: ResizeObserver;
  showMenu = false;
  isStacked = false;
  totalButtonWidth = 0;

  ngAfterViewInit(): void {
    this.initResizeObserver();

    // Escuta mudanças na lista de `buttonElements` para garantir que estão prontos
    this.buttonElements.changes.subscribe(() => this.checkLayout());

    // Usar um pequeno atraso para garantir a renderização completa
    setTimeout(() => this.checkLayout(), 0);
  }

  ngOnDestroy(): void {
    this._resizeObserver.disconnect();
  }

  private initResizeObserver(): void {
    this._resizeObserver = new ResizeObserver(() => this.checkLayout());
    this._resizeObserver.observe(this.buttonContainer.nativeElement);
  }

  /**
   * Verificar layout para redimensionamento
   */
  private checkLayout(): void {
    // Garante que as visualizações estão atualizadas e os elementos estão prontos
    this._cd.detectChanges();

    const containerWidth = this.buttonContainer.nativeElement.getBoundingClientRect().width;
    const totalButtonWidth = this.calculateTotalButtonWidth();

    if (totalButtonWidth > containerWidth) {
      if (this.useMenu) {
        this.showMenu = true;
        this.isStacked = false;
      } else {
        this.showMenu = false;
        this.isStacked = true;
      }
    } else {
      this.showMenu = false;
      this.isStacked = false;
    }

    this.totalButtonWidth = totalButtonWidth;
  }

  /**
   * Verificar a tamanho que todos os botões ocupam renderizados
   */
  private calculateTotalButtonWidth(): number {
    let totalWidth = 0;
    const elementos = document.getElementsByTagName('hmax-button');

    Array.from(elementos).forEach((elemento) => {
      if (elemento instanceof HTMLElement) {
        totalWidth += elemento.offsetWidth;
      }
    });

    return totalWidth ? totalWidth : this.totalButtonWidth;
  }

  onClick(event: string): void {
    this.buttonClick.emit(event);
  }
}
